/**
 * FloorPlan — Plano visual del salón con drag-and-drop nativo.
 *
 * Modos:
 *  • Vista  — Muestra colores por estado; click abre el POS en esa mesa.
 *  • Editar — Permite arrastrar mesas y cambiar forma; guarda al soltar.
 *
 * Implementado con Pointer Events (sin librerías de DnD externas).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  ArrowLeft, Edit2, Eye, Save, RotateCcw, Circle, Square, RefreshCw, Loader2,
} from 'lucide-react';

import { getTables, updateTable } from '../../services/TableService';
import { api } from '../../utils/api';
import { echo } from '../../lib/echo';
import type { Table } from '../../types/@types/table';

// ── Constants ─────────────────────────────────────────────────────────────────

const TABLE_W = 80;   // px
const TABLE_H = 80;
const GRID    = 20;   // snap grid

const STATUS_STYLES: Record<Table['status'], { bg: string; ring: string; label: string; dot: string }> = {
  Disponible: { bg: 'bg-emerald-500', ring: 'ring-emerald-300', label: 'Libre',    dot: 'bg-emerald-400' },
  Ocupada:    { bg: 'bg-red-500',     ring: 'ring-red-300',     label: 'Ocupada',  dot: 'bg-red-400'     },
  Reservada:  { bg: 'bg-amber-400',   ring: 'ring-amber-300',   label: 'Reservada',dot: 'bg-amber-400'   },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function snap(v: number) { return Math.round(v / GRID) * GRID; }

function defaultPos(index: number) {
  const col = index % 5;
  const row = Math.floor(index / 5);
  return { posX: 60 + col * 120, posY: 60 + row * 130 };
}

// ── Local position state ──────────────────────────────────────────────────────

type LocalTable = Table & { posX: number; posY: number; shape: 'square' | 'round' };

function hydrate(tables: Table[]): LocalTable[] {
  return tables.map((t, i) => ({
    ...t,
    posX:  t.posX  ?? defaultPos(i).posX,
    posY:  t.posY  ?? defaultPos(i).posY,
    shape: t.shape ?? 'square',
  }));
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function FloorPlan() {
  const navigate     = useNavigate();
  const queryClient  = useQueryClient();
  const canvasRef    = useRef<HTMLDivElement>(null);

  const [editMode,  setEditMode]  = useState(false);
  const [localTables, setLocalTables] = useState<LocalTable[]>([]);
  const [dirty,     setDirty]     = useState(false);
  const [saving,    setSaving]    = useState(false);

  // Drag state
  const dragging = useRef<{ id: string; startX: number; startY: number; origX: number; origY: number } | null>(null);

  // ── Data fetching ────────────────────────────────────────────────────────
  const { data: tables = [], isLoading, refetch } = useQuery<Table[]>({
    queryKey: ['tables'],
    queryFn: getTables,
    refetchInterval: editMode ? false : 15_000,
  });

  // Sync local state when server data arrives (only if not dirty)
  useEffect(() => {
    if (!dirty) setLocalTables(hydrate(tables));
  }, [tables, dirty]);

  // Real-time WebSocket
  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['tables'] });
    };
    const ch = echo.channel('pos-updates');
    ch.listen('TableUpdated', invalidate);
    return () => { echo.leave('pos-updates'); };
  }, [queryClient]);

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const onPointerDown = useCallback((e: React.PointerEvent, id: string) => {
    if (!editMode) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const t = localTables.find(t => t.id === id)!;
    dragging.current = {
      id,
      startX: e.clientX,
      startY: e.clientY,
      origX:  t.posX,
      origY:  t.posY,
    };
  }, [editMode, localTables]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    const { id, startX, startY, origX, origY } = dragging.current;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    const newX = snap(Math.max(0, origX + dx));
    const newY = snap(Math.max(0, origY + dy));

    setLocalTables(prev => prev.map(t => t.id === id ? { ...t, posX: newX, posY: newY } : t));
    setDirty(true);
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = null;
  }, []);

  // ── Shape toggle ──────────────────────────────────────────────────────────
  const toggleShape = (id: string) => {
    setLocalTables(prev => prev.map(t =>
      t.id === id ? { ...t, shape: t.shape === 'square' ? 'round' : 'square' } : t
    ));
    setDirty(true);
  };

  // ── Save positions ────────────────────────────────────────────────────────
  const save = async () => {
    setSaving(true);
    try {
      await api.post('/tables/bulk-positions', {
        tables: localTables.map(t => ({ id: t.id, posX: t.posX, posY: t.posY })),
      });
      // Save shapes individually (they're part of the normal update)
      await Promise.all(
        localTables.map(t =>
          api.patch(`/tables/${t.id}/position`, { posX: t.posX, posY: t.posY, shape: t.shape })
        )
      );
      setDirty(false);
      toast.success('Plano guardado');
      queryClient.invalidateQueries({ queryKey: ['tables'] });
    } catch {
      toast.error('Error al guardar posiciones');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setLocalTables(hydrate(tables));
    setDirty(false);
  };

  // ── Click table (view mode) ───────────────────────────────────────────────
  const handleClick = (table: LocalTable) => {
    if (editMode) return;
    navigate('/pos', { state: { tableId: table.id } });
  };

  // ── Canvas size (auto from rightmost/bottommost table) ────────────────────
  const canvasW = Math.max(600, ...localTables.map(t => t.posX + TABLE_W + 40));
  const canvasH = Math.max(400, ...localTables.map(t => t.posY + TABLE_H + 40));

  // ── Legends ───────────────────────────────────────────────────────────────
  const counts = {
    Disponible: localTables.filter(t => t.status === 'Disponible').length,
    Ocupada:    localTables.filter(t => t.status === 'Ocupada').length,
    Reservada:  localTables.filter(t => t.status === 'Reservada').length,
  };

  return (
    <div className="flex h-dvh flex-col bg-gray-100 dark:bg-neutral-900 overflow-hidden">

      {/* ── Toolbar ── */}
      <header className="flex items-center gap-3 bg-white dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-700 px-4 py-3 shrink-0 shadow-sm">
        <button
          onClick={() => navigate('/tables')}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-700 transition"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="flex-1">
          <h1 className="font-bold text-gray-900 dark:text-white">Plano del salón</h1>
          <p className="text-xs text-gray-500">
            {localTables.length} mesas · {counts.Disponible} libres · {counts.Ocupada} ocupadas · {counts.Reservada} reservadas
          </p>
        </div>

        {/* Legend */}
        <div className="hidden sm:flex items-center gap-3">
          {(Object.entries(STATUS_STYLES) as [Table['status'], typeof STATUS_STYLES[Table['status']]][]).map(([s, style]) => (
            <div key={s} className="flex items-center gap-1.5">
              <div className={`h-3 w-3 rounded-full ${style.dot}`} />
              <span className="text-xs text-gray-600 dark:text-gray-400">{style.label} ({counts[s]})</span>
            </div>
          ))}
        </div>

        <button
          onClick={() => { refetch(); }}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 dark:border-neutral-600 hover:bg-gray-50 transition"
          title="Refrescar"
        >
          <RefreshCw size={15} />
        </button>

        {/* Edit / View toggle */}
        <button
          onClick={() => { setEditMode(v => !v); if (!editMode) discard(); }}
          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${
            editMode
              ? 'bg-amber-500 text-white hover:bg-amber-600'
              : 'bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200'
          }`}
        >
          {editMode ? <><Eye size={15} /> Ver</> : <><Edit2 size={15} /> Editar</>}
        </button>

        {/* Save / Discard (edit mode) */}
        {editMode && dirty && (
          <>
            <button
              onClick={discard}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium hover:bg-gray-50 transition"
            >
              <RotateCcw size={14} /> Descartar
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-60 transition"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar
            </button>
          </>
        )}
      </header>

      {/* ── Edit mode banner ── */}
      {editMode && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700 px-4 py-2 text-xs text-amber-700 dark:text-amber-300 font-medium flex items-center gap-2 shrink-0">
          <Edit2 size={12} />
          Modo edición: arrastra las mesas para reorganizarlas. Haz clic en ◎ para cambiar entre forma cuadrada y redonda.
        </div>
      )}

      {/* ── Canvas ── */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 size={32} className="animate-spin text-primary" />
          </div>
        ) : (
          <div
            ref={canvasRef}
            className="relative"
            style={{
              width: canvasW,
              height: canvasH,
              backgroundImage: editMode
                ? 'radial-gradient(circle, #d1d5db 1px, transparent 1px)'
                : 'none',
              backgroundSize: `${GRID}px ${GRID}px`,
              cursor: editMode ? 'default' : 'auto',
            }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          >
            {localTables.map(table => {
              const style = STATUS_STYLES[table.status];
              const isRound = table.shape === 'round';

              return (
                <div
                  key={table.id}
                  style={{
                    position: 'absolute',
                    left: table.posX,
                    top:  table.posY,
                    width:  TABLE_W,
                    height: TABLE_H,
                    userSelect: 'none',
                    touchAction: 'none',
                    cursor: editMode ? 'grab' : 'pointer',
                    transition: dragging.current?.id === table.id ? 'none' : 'left 0.15s, top 0.15s',
                  }}
                  onPointerDown={e => onPointerDown(e, table.id)}
                  onClick={() => handleClick(table)}
                >
                  {/* Table shape */}
                  <div
                    className={`
                      absolute inset-0 flex flex-col items-center justify-center gap-1
                      shadow-lg transition-all
                      ${style.bg} text-white font-bold
                      ${isRound ? 'rounded-full' : 'rounded-2xl'}
                      ${!editMode ? `ring-4 ${style.ring} ring-offset-2 hover:scale-105` : ''}
                    `}
                  >
                    <span className="text-xl leading-none">{table.number}</span>
                    <span className="text-[10px] font-semibold opacity-90 leading-none">
                      {table.capacity} pers.
                    </span>
                    <span className="text-[9px] opacity-80 leading-none">{style.label}</span>
                  </div>

                  {/* Shape toggle button (edit mode only) */}
                  {editMode && (
                    <button
                      type="button"
                      onPointerDown={e => e.stopPropagation()}
                      onClick={e => { e.stopPropagation(); toggleShape(table.id); }}
                      className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white border border-gray-200 shadow text-gray-600 hover:bg-gray-50 z-10"
                      title={isRound ? 'Cambiar a cuadrada' : 'Cambiar a redonda'}
                    >
                      {isRound ? <Square size={11} /> : <Circle size={11} />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
